import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  HStack,
  VStack,
  Spinner,
  Center,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Tooltip,
  Textarea,
  Switch,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  Checkbox,
} from '@chakra-ui/react';
import { FiEdit2, FiEye, FiUserPlus, FiUpload } from 'react-icons/fi';
import { getAllUsers, updateUser, createUser, deleteUser, User } from '../services/firestore';
import ExportModal from './Exports/ExportModal';
import { 
  importUsersFromCSV, 
  importUsersFromJSON, 
  readFileContent, 
  detectFileType,
  type ImportResult 
} from '../services/importService';

const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<Partial<User>>({});
  const [creatingUser, setCreatingUser] = useState<Partial<User>>({});
  const [updating, setUpdating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Notification channel prefs
  const [editingPrefs, setEditingPrefs] = useState<{ push?: boolean; email?: boolean; sms?: boolean }>({});
  const [creatingPrefs, setCreatingPrefs] = useState<{ push?: boolean; email?: boolean; sms?: boolean }>({ push: true, email: false, sms: false });
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult<any> | null>(null);
  const [importing, setImporting] = useState(false);
  
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const toast = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUsers();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error loading users',
        description: 'Failed to fetch users data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const validateUser = (user: Partial<User>, isCreate: boolean = false): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!user.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!user.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!user.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(user.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!user.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!user.role) {
      newErrors.role = 'Role is required';
    }

    if (isCreate && !user.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (isCreate && user.password && user.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    return newErrors;
  };

  const handleCreateUser = () => {
    setCreatingUser({});
    setErrors({});
    onCreateOpen();
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditingUser({ ...user });
    setEditingPrefs({
      push: (user as any).notificationPreferences?.push ?? true,
      email: (user as any).notificationPreferences?.email ?? false,
      sms: (user as any).notificationPreferences?.sms ?? false,
    });
    setErrors({});
    onEditOpen();
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    onViewOpen();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV or JSON file to import',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const fileContent = await readFileContent(importFile);
      const fileType = detectFileType(importFile.name);

      let result: ImportResult<any>;

      if (fileType === 'csv') {
        result = importUsersFromCSV(fileContent, { skipFirstRow: true });
      } else if (fileType === 'json') {
        result = importUsersFromJSON(fileContent);
      } else {
        throw new Error('Unsupported file type. Please use CSV or JSON.');
      }

      setImportResult(result);

      if (result.data.length === 0) {
        toast({
          title: 'No valid users found',
          description: 'The file contains no valid user data to import',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // If there are errors but also valid data, show warning
      if (result.errors.length > 0) {
        toast({
          title: 'Import completed with errors',
          description: `Imported ${result.importedRows} users, ${result.skippedRows} skipped`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Failed to import users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importResult || importResult.data.length === 0) return;

    setImporting(true);
    const errors: string[] = [];
    let successCount = 0;

    try {
      // Import users in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < importResult.data.length; i += batchSize) {
        const batch = importResult.data.slice(i, i + batchSize);
        // eslint-disable-next-line no-loop-func
        await Promise.all(
          batch.map(async (userData) => {
            try {
              await createUser({
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                role: userData.role as 'customer' | 'technician' | 'admin',
                phone: userData.phone,
                isActive: userData.isActive !== false,
              });
              successCount++;
            } catch (error) {
              errors.push(`Failed to import ${userData.email}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          })
        );
      }

      // Refresh users list
      await fetchUsers();

      // Close modal and reset
      setIsImportOpen(false);
      setImportFile(null);
      setImportResult(null);

      if (errors.length > 0) {
        toast({
          title: 'Import completed with errors',
          description: `Imported ${successCount} users, ${errors.length} failed`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Import successful',
          description: `Successfully imported ${successCount} users`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error confirming import:', error);
      toast({
        title: 'Import failed',
        description: 'Failed to import users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleSaveUser = async (isCreate: boolean = false) => {
    const userData = isCreate ? creatingUser : editingUser;
    const validationErrors = validateUser(userData, isCreate);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (isCreate) {
        setCreating(true);
        const payload: any = { ...userData, notificationPreferences: creatingPrefs };
        await createUser(payload as User);
        toast({
          title: 'User created',
          description: 'User has been created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onCreateClose();
      } else {
        setUpdating(true);
        const payload: any = { ...userData, notificationPreferences: editingPrefs };
        await updateUser(selectedUser!.id, payload);
        setUsers(users.map(user => 
          user.id === selectedUser!.id 
            ? { ...user, ...payload }
            : user
        ));
        toast({
          title: 'User updated',
          description: 'User has been updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onEditClose();
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Save failed',
        description: 'Failed to save user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await deleteUser(userId);
      setUsers(users.filter(user => user.id !== userId));
      toast({
        title: 'User deleted',
        description: 'User has been deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Delete failed',
        description: 'Failed to delete user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'technician':
        return 'blue';
      case 'customer':
        return 'green';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          User Management
        </Heading>
        <HStack>
          {selectedUserIds.length > 0 && (
            <HStack>
              <Button size="sm" colorScheme="green" minH="44px" onClick={async()=>{
                setBulkLoading(true);
                try {
                  await Promise.all(selectedUserIds.map(id => updateUser(id, { isActive: true })));
                  setUsers(users.map(u => selectedUserIds.includes(u.id) ? { ...u, isActive: true } : u));
                  setSelectedUserIds([]);
                  toast({ title: 'Users activated', status: 'success', duration: 2000, isClosable: true });
                } finally { setBulkLoading(false); }
              }} isLoading={bulkLoading}>Activate</Button>
              <Button size="sm" colorScheme="red" variant="outline" minH="44px" onClick={async()=>{
                setBulkLoading(true);
                try {
                  await Promise.all(selectedUserIds.map(id => updateUser(id, { isActive: false })));
                  setUsers(users.map(u => selectedUserIds.includes(u.id) ? { ...u, isActive: false } : u));
                  setSelectedUserIds([]);
                  toast({ title: 'Users deactivated', status: 'success', duration: 2000, isClosable: true });
                } finally { setBulkLoading(false); }
              }} isLoading={bulkLoading}>Deactivate</Button>
              <HStack>
                <Select size="sm" placeholder="Change role" value={bulkRole} onChange={(e)=> setBulkRole(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="technician">Technician</option>
                  <option value="customer">Customer</option>
                </Select>
                <Button size="sm" minH="44px" onClick={async()=>{
                  if (!bulkRole) return;
                  setBulkLoading(true);
                  try {
                    await Promise.all(selectedUserIds.map(id => updateUser(id, { role: bulkRole as any })));
                    setUsers(users.map(u => selectedUserIds.includes(u.id) ? { ...u, role: bulkRole as any } : u));
                    setSelectedUserIds([]); setBulkRole('');
                    toast({ title: 'Roles updated', status: 'success', duration: 2000, isClosable: true });
                  } finally { setBulkLoading(false); }
                }} isLoading={bulkLoading}>Apply</Button>
              </HStack>
            </HStack>
          )}
          <Button 
            size="sm" 
            variant="outline" 
            minH="44px" 
            leftIcon={React.createElement(FiUpload as React.ElementType)}
            onClick={() => setIsImportOpen(true)}
          >
            Import
          </Button>
          <Button size="sm" variant="outline" minH="44px" onClick={()=> setIsExportOpen(true)}>Export</Button>
          <Button 
          leftIcon={React.createElement(FiUserPlus as React.ElementType)} 
          colorScheme="blue"
          onClick={handleCreateUser}
        >
          Create User
        </Button>
        </HStack>
      </Flex>

      {/* Search and Filter Controls */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormLabel fontSize="sm">Search Users</FormLabel>
            <InputGroup>
              <InputLeftElement>
                {React.createElement(FiUserPlus as React.ElementType)}
              </InputLeftElement>
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Box>
          <Box minW="200px">
            <FormLabel fontSize="sm">Filter by Role</FormLabel>
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="technician">Technician</option>
              <option value="customer">Customer</option>
            </Select>
          </Box>
        </HStack>
      </Box>

      {/* Users Table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
        <Box 
          overflowX="auto" 
          overflowY="auto"
          maxH="calc(100vh - 400px)"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Table variant="simple" minW="100%">
          <Thead>
            <Tr>
              <Th>
                <Checkbox
                  isChecked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                  isIndeterminate={selectedUserIds.length > 0 && selectedUserIds.length < filteredUsers.length}
                  onChange={(e: any)=> setSelectedUserIds(e.target.checked ? filteredUsers.map(u=>u.id) : [])}
                />
              </Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Joined</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredUsers.map((user) => (
              <Tr key={user.id}>
                <Td>
                  <Checkbox
                    isChecked={selectedUserIds.includes(user.id)}
                    onChange={(e: any)=> setSelectedUserIds(e.target.checked ? [...selectedUserIds, user.id] : selectedUserIds.filter(id=>id!==user.id))}
                  />
                </Td>
                <Td fontWeight="bold">
                  {user.firstName} {user.lastName}
                </Td>
                <Td>{user.email}</Td>
                <Td>{user.phone || 'N/A'}</Td>
                <Td>
                  <Badge colorScheme={getRoleColor(user.role || '')}>
                    {user.role}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={user.isActive ? 'green' : 'red'}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>{formatDate(user.createdAt)}</Td>
                <Td>
                  <HStack spacing={2}>
                    <Tooltip label="View Details">
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiEye as React.ElementType)}
                        aria-label="View user"
                        variant="ghost"
                        onClick={() => handleViewUser(user)}
                      />
                    </Tooltip>
                    <Tooltip label="Edit User">
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiEdit2 as React.ElementType)}
                        aria-label="Edit user"
                        variant="ghost"
                        onClick={() => handleEditUser(user)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>

        {filteredUsers.length === 0 && (
          <Center py={8}>
            <Text color="gray.500">No users found</Text>
          </Center>
        )}
      </Box>

      {/* Create User Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.firstName}>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={creatingUser.firstName || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, firstName: e.target.value})}
                  placeholder="Enter first name"
                />
                <FormErrorMessage>{errors.firstName}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.lastName}>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={creatingUser.lastName || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, lastName: e.target.value})}
                  placeholder="Enter last name"
                />
                <FormErrorMessage>{errors.lastName}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={creatingUser.email || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, email: e.target.value})}
                  placeholder="Enter email address"
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.phone}>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={creatingUser.phone || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, phone: e.target.value})}
                  placeholder="Enter phone number"
                />
                <FormErrorMessage>{errors.phone}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.role}>
                <FormLabel>Role</FormLabel>
                <Select
                  value={creatingUser.role || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, role: e.target.value as any})}
                  placeholder="Select role"
                >
                  <option value="admin">Admin</option>
                  <option value="technician">Technician</option>
                  <option value="customer">Customer</option>
                </Select>
                <FormErrorMessage>{errors.role}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.password}>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={creatingUser.password || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, password: e.target.value})}
                  placeholder="Enter password"
                />
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>Address</FormLabel>
                <Textarea
                  value={creatingUser.address || ''}
                  onChange={(e) => setCreatingUser({...creatingUser, address: e.target.value})}
                  placeholder="Enter address"
                  rows={3}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Active Status</FormLabel>
                <Switch
                  isChecked={creatingUser.isActive !== false}
                  onChange={(e) => setCreatingUser({...creatingUser, isActive: e.target.checked})}
                />
              </FormControl>
              <Box w="full" pt={2}>
                <Heading size="sm" mb={2}>Notification Preferences</Heading>
                <HStack>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel mb="0">Push</FormLabel>
                    <Switch isChecked={creatingPrefs.push !== false} onChange={(e)=> setCreatingPrefs(prev=>({ ...prev, push: e.target.checked }))} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel mb="0">Email</FormLabel>
                    <Switch isChecked={!!creatingPrefs.email} onChange={(e)=> setCreatingPrefs(prev=>({ ...prev, email: e.target.checked }))} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel mb="0">SMS</FormLabel>
                    <Switch isChecked={!!creatingPrefs.sms} onChange={(e)=> setCreatingPrefs(prev=>({ ...prev, sms: e.target.checked }))} />
                  </FormControl>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={() => handleSaveUser(true)}
              isLoading={creating}
              loadingText="Creating"
            >
              Create User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.firstName}>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={editingUser.firstName || ''}
                  onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                />
                <FormErrorMessage>{errors.firstName}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.lastName}>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={editingUser.lastName || ''}
                  onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                />
                <FormErrorMessage>{errors.lastName}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.phone}>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                />
                <FormErrorMessage>{errors.phone}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.role}>
                <FormLabel>Role</FormLabel>
                <Select
                  value={editingUser.role || ''}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value as any})}
                >
                  <option value="admin">Admin</option>
                  <option value="technician">Technician</option>
                  <option value="customer">Customer</option>
                </Select>
                <FormErrorMessage>{errors.role}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>Address</FormLabel>
                <Textarea
                  value={editingUser.address || ''}
                  onChange={(e) => setEditingUser({...editingUser, address: e.target.value})}
                  rows={3}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Active Status</FormLabel>
                <Switch
                  isChecked={editingUser.isActive !== false}
                  onChange={(e) => setEditingUser({...editingUser, isActive: e.target.checked})}
                />
              </FormControl>
              <Box w="full" pt={2}>
                <Heading size="sm" mb={2}>Notification Preferences</Heading>
                <HStack>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel mb="0">Push</FormLabel>
                    <Switch isChecked={editingPrefs.push !== false} onChange={(e)=> setEditingPrefs(prev=>({ ...prev, push: e.target.checked }))} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel mb="0">Email</FormLabel>
                    <Switch isChecked={!!editingPrefs.email} onChange={(e)=> setEditingPrefs(prev=>({ ...prev, email: e.target.checked }))} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel mb="0">SMS</FormLabel>
                    <Switch isChecked={!!editingPrefs.sms} onChange={(e)=> setEditingPrefs(prev=>({ ...prev, sms: e.target.checked }))} />
                  </FormControl>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              variant="outline"
              mr={3}
              onClick={() => selectedUser && handleDeleteUser(selectedUser.id)}
            >
              Delete User
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={() => handleSaveUser(false)}
              isLoading={updating}
              loadingText="Updating"
            >
              Update User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View User Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>User Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedUser && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" fontSize="lg">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </Text>
                  <Text color="gray.600">{selectedUser.email}</Text>
                </Box>
                <HStack>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Phone</Text>
                    <Text>{selectedUser.phone || 'N/A'}</Text>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Role</Text>
                    <Badge colorScheme={getRoleColor(selectedUser.role || '')}>
                      {selectedUser.role}
                    </Badge>
                  </Box>
                </HStack>
                <Box>
                  <Text fontWeight="semibold">Address</Text>
                  <Text>{selectedUser.address || 'N/A'}</Text>
                </Box>
                <HStack>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Status</Text>
                    <Badge colorScheme={selectedUser.isActive ? 'green' : 'red'}>
                      {selectedUser.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold">Joined</Text>
                    <Text>{formatDate(selectedUser.createdAt)}</Text>
                  </Box>
                </HStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Export Modal */}
      <ExportModal
        isOpen={isExportOpen}
        onClose={()=> setIsExportOpen(false)}
        data={filteredUsers as any}
        title="Export Users"
        defaultFilename="users_export"
      />

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
        setIsImportOpen(false);
        setImportFile(null);
        setImportResult(null);
      }} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Users</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Select File (CSV or JSON)</FormLabel>
                <Input
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileSelect}
                  disabled={importing}
                />
                <Text fontSize="sm" color="gray.500" mt={2}>
                  CSV format: Email, First Name, Last Name, Role, Phone, Active<br/>
                  JSON format: Array of user objects with the same fields
                </Text>
              </FormControl>

              {importFile && (
                <Box>
                  <Text fontWeight="semibold">Selected file:</Text>
                  <Text>{importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)</Text>
                </Box>
              )}

              {importResult && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>Import Preview:</Text>
                  <VStack align="stretch" spacing={2}>
                    <Text>Total rows: {importResult.totalRows}</Text>
                    <Text color="green.500">Valid users: {importResult.importedRows}</Text>
                    {importResult.skippedRows > 0 && (
                      <Text color="orange.500">Skipped rows: {importResult.skippedRows}</Text>
                    )}
                    {importResult.errors.length > 0 && (
                      <Box>
                        <Text fontWeight="semibold" color="red.500" mb={1}>
                          Errors ({importResult.errors.length}):
                        </Text>
                        <Box maxH="200px" overflowY="auto" bg="red.50" p={2} borderRadius="md">
                          {importResult.errors.map((error, idx) => (
                            <Text key={idx} fontSize="sm" color="red.700">
                              {error}
                            </Text>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {importResult.warnings.length > 0 && (
                      <Box>
                        <Text fontWeight="semibold" color="orange.500" mb={1}>
                          Warnings ({importResult.warnings.length}):
                        </Text>
                        <Box maxH="200px" overflowY="auto" bg="orange.50" p={2} borderRadius="md">
                          {importResult.warnings.map((warning, idx) => (
                            <Text key={idx} fontSize="sm" color="orange.700">
                              {warning}
                            </Text>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setIsImportOpen(false);
                setImportFile(null);
                setImportResult(null);
              }}
              disabled={importing}
            >
              Cancel
            </Button>
            {!importResult ? (
              <Button
                colorScheme="blue"
                onClick={handleImport}
                isLoading={importing}
                disabled={!importFile}
              >
                Preview Import
              </Button>
            ) : (
              <Button
                colorScheme="green"
                onClick={handleConfirmImport}
                isLoading={importing}
                disabled={importResult.importedRows === 0}
              >
                Import {importResult.importedRows} Users
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UsersManagement; 